import CampaignAPI from '@/api/campaign.api.js'
import SoftphoneAPI, { SOFTPHONE_STATE_CHANGE_REASON } from '@/api/softphone.api'
import { currencyFormatter } from '@/utils/formatCurrency'
import { getMinBidMessage, getStaticCampaignMinBid } from '@/utils/campaignMinBid'
import cloneDeep from 'lodash.clonedeep'

// Its own copy rather than an import from creative.store, which pulls the whole store graph in
// behind it - auth.store.js keeps one for the same reason.
const MEDICARE_FLAG = 'MEDICARE'

export const PAUSED_STATUS = 'Paused'
export const ACTIVE_STATUS = 'Active'
export const IN_REVIEW_STATUS = 'InReview'
export const RTB_ID_REGEX = /^[a-z0-9]{32}$/

export const campaignRoutingTypes = {
  Static: 'Static',
  RingbaRTB: 'RingbaRTB',
  Programmatic: 'Programmatic'
}

export const destinationTypes = {
  Number: 'Number',
  SIP: 'SIP',
  Softphone: 'Softphone'
}

// Softphone campaigns are always US; the destination is provisioned per agent rather than
// entered, so the buyer is never asked for a country.
export const SOFTPHONE_CAMPAIGN_COUNTRY_ID = 'US'

// Preferred when the category offers a choice - the buyer can still pick another.
export const SOFTPHONE_CAMPAIGN_PREFERRED_LANGUAGE_ID = 'en'

// Used when neither the account nor the browser names a US time zone. The campaign's time zone is
// what its daily and hourly budget windows are measured in, so it has to be something.
export const SOFTPHONE_CAMPAIGN_FALLBACK_TIME_ZONE_ID = 'Eastern Standard Time'

// The account time zone is set on the Account > Timezone page and can simply be unset, but the
// softphone create page has no Schedule section to ask on. The browser's zone is the next best
// guess at where the agent is; these are the Windows ids the API and the timezone list use, for
// the US zones a US-only campaign can be in.
const US_TIME_ZONE_IDS_BY_BROWSER_ZONE = Object.freeze({
  'America/New_York': 'Eastern Standard Time',
  'America/Detroit': 'Eastern Standard Time',
  'America/Indiana/Indianapolis': 'Eastern Standard Time',
  'America/Kentucky/Louisville': 'Eastern Standard Time',
  'America/Chicago': 'Central Standard Time',
  'America/Indiana/Knox': 'Central Standard Time',
  'America/Menominee': 'Central Standard Time',
  'America/Denver': 'Mountain Standard Time',
  'America/Boise': 'Mountain Standard Time',
  'America/Phoenix': 'US Mountain Standard Time',
  'America/Los_Angeles': 'Pacific Standard Time',
  'America/Anchorage': 'Alaskan Standard Time',
  'America/Juneau': 'Alaskan Standard Time',
  'America/Nome': 'Alaskan Standard Time',
  'Pacific/Honolulu': 'Hawaiian Standard Time'
})

// The account's own time zone always wins - it is what the rest of the buyer's campaigns and
// reports use, so a softphone campaign must not quietly disagree with it.
export function resolveSoftphoneCampaignTimeZoneId(accountTimeZoneId, browserTimeZone) {
  if (accountTimeZoneId) {
    return accountTimeZoneId
  }

  return US_TIME_ZONE_IDS_BY_BROWSER_ZONE[browserTimeZone] ?? SOFTPHONE_CAMPAIGN_FALLBACK_TIME_ZONE_ID
}

// What the browser thinks the agent's time zone is, as an IANA name. Guarded: Intl is everywhere
// this app runs, but a resolved zone is not worth throwing a page away for.
export function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    return null
  }
}

// Which categories a softphone campaign may be created for. Two are left out, both because the
// page cannot show what the category implies:
//   - CPA-only bills on postback conversions and hides Minimum Duration, and this page has no
//     other place to put those terms.
//   - Medicare routes on approved creatives instead of traffic sources, and a softphone campaign
//     has no creative flow at all - the campaign would be enabled but reachable by no seller.
export function isSoftphoneEligibleCategory(category) {
  return Boolean(category)
    && category.isCpaConversionOnly !== true
    && !(category.flags ?? []).includes(MEDICARE_FLAG)
}

// Only an approved campaign can take calls: it is the approval that creates the Ringba target the
// calls arrive through. A new campaign is InReview and a rejected one never will be, and neither
// is paused - so without this check going live on one is an idempotent no-op that leaves the agent
// waiting in silence. An allow-list, so Expired and Deleted are refused too.
export function canSoftphoneCampaignGoLive(campaign) {
  return campaign?.status === ACTIVE_STATUS || campaign?.status === PAUSED_STATUS
}

/**
 * Why going live now would put the agent on a silent shift, or null when it would not.
 *
 * The approval check above asks whether the campaign may EVER take calls. This asks whether it can
 * take one THIS MINUTE, and every answer is temporary: the account is out of funds, or it has spent
 * what it allowed itself for the day. Ringba caps the buyer in each case and routes nothing, and
 * the softphone would register, go green and sit there - which is exactly the state
 * `SOFTPHONE_STATUS.BALANCE_TOO_LOW` exists to stop the agent being left in AFTER going live. This
 * stops them starting a shift that cannot ring in the first place.
 *
 * Returns the message rather than a code, so the button's tooltip, the refusal toast and any future
 * caller all read from one copy of the wording.
 *
 * `accountBalance` is the buyer balance endpoint's response, held in `billing.accountBalance` and
 * refreshed every 45 seconds by the header - see AccountSummaryHeader. Both of its flags are the
 * SERVER's answer: which minimum the account has to hold and what it has spent today are its
 * business, and recomputing either here would be a second, worse copy of that rule.
 *
 * DAILY ONLY, and not for want of asking. A campaign can carry an HOURLY budget as well
 * (`budget.hourlyBudgetSelected` / `hourlyBudgetInUSD`), but nothing reports whether it has been
 * reached: RingbaX keeps a daily ledger of its own and answers off that, while the hourly figure is
 * only forwarded to Ringba as the target's hourly cap on revenue and enforced there. So there is no
 * `isHourlyBudgetReached` to read, on the account or the campaign, and an agent CAN still go live
 * into an hour that is already capped. Closing that needs an hourly ledger server-side, or a way to
 * ask Ringba for the target's live cap state - not a change here.
 */
export function getSoftphoneGoLiveBlockedReason(campaign, accountBalance) {
  // Ahead of the budget for the same reason the dashboard banner puts it there: a daily budget
  // resets tomorrow on its own, and a balance below the account's minimum does not.
  if (accountBalance?.isBalanceLow === true) {
    return 'Your account balance is too low to buy calls - add funds before going live'
  }

  if (accountBalance?.isDailyBudgetReached === true) {
    return 'Your account daily budget is used up - no calls can be bought until it resets or you raise it'
  }

  /**
   * The campaign's own budget rather than the account's, and it is NEVER on a list row: the list
   * endpoint returns the `BuyerCampaign` entity, which has no such field, while the flag is set
   * only by `GetBuyerCampaignAsync` on the `BuyerCampaignDTO`. Reading it off a row the list
   * supplied is therefore always `undefined` - which is how a campaign over its daily budget was
   * still offering Go Live on staging.
   *
   * So the caller has to have FETCHED the campaign for this to mean anything - see
   * `fetchBuyerCampaignBudgetState`, and `goLiveForCampaign`, which merges that answer in before
   * asking. Absent still has to mean "no reason to refuse", never "blocked", or a campaign whose
   * budget could not be read would lock the agent out of their own shift.
   */
  if (campaign?.isDailyBudgetReached === true) {
    return 'This campaign has used up its daily budget - no calls can be bought until it resets or you raise it'
  }

  return null
}

// Shown when the agent already holds the campaign they are trying to create again. Worded like
// the API's own refusal, because either one can be the first thing the agent sees: this one on
// submit, the API's if a campaign was created elsewhere after this page loaded.
export const SOFTPHONE_DUPLICATE_CAMPAIGN_MESSAGE =
  'You already have a campaign for this category and language. Use that campaign instead of creating another one.'

// Appended to a category or language the agent has already used, next to disabling it.
export const SOFTPHONE_COMBO_TAKEN_SUFFIX = '(already have a campaign)'

// A softphone campaign's destination is the single agent it rings - the one who created it - so
// the API allows one campaign per agent per category/country/language. This is the key that
// rule turns on, mirroring the destination id the API builds server-side.
function buildComboKey(categoryId, countryId, languageId) {
  return `${categoryId ?? ''}|${countryId ?? ''}|${languageId ?? ''}`
}

// The page only ever creates US campaigns, so a combination it can reach is a US one.
export function buildSoftphoneCampaignComboKey(categoryId, languageId) {
  return buildComboKey(categoryId, SOFTPHONE_CAMPAIGN_COUNTRY_ID, languageId)
}

// The campaigns list covers the whole account, including the other agents' softphone campaigns.
// Those do not constrain this agent - each agent gets their own campaign per combination - so
// only the ones this agent created are taken. Each campaign's own country goes into the key
// rather than an assumed US: a campaign for another country blocks nothing this page can create.
export function getTakenSoftphoneCampaignCombos(campaigns, agentUserId) {
  if (!agentUserId) {
    return []
  }

  return (campaigns ?? [])
    .filter(campaign => isSoftphoneCampaign(campaign) && campaign.createdBy === agentUserId)
    .map(campaign => buildComboKey(campaign.category?.id, campaign.country?.id, campaign.language?.id))
}

export function isSoftphoneCampaignComboTaken(takenCombos, categoryId, languageId) {
  return (takenCombos ?? []).includes(buildSoftphoneCampaignComboKey(categoryId, languageId))
}

export function getTakenSoftphoneLanguageIds(takenCombos, categoryId, categoryLanguages) {
  return (categoryLanguages ?? [])
    .filter(language => isSoftphoneCampaignComboTaken(takenCombos, categoryId, language.id))
    .map(language => language.id)
}

// A category is out only once every language it offers is taken - with one language left the
// agent can still create a campaign there. A category with no configured languages is left
// alone: there is no combination to have taken.
export function isSoftphoneCategoryTaken(takenCombos, category) {
  const languages = category?.languages ?? []

  return languages.length > 0
    && languages.every(language => isSoftphoneCampaignComboTaken(takenCombos, category.id, language.id))
}

// A category with one configured language leaves nothing to choose, so take it. With several,
// keep whatever is already selected if the new category still supports it, else fall back to
// English, else make the buyer pick (the API rejects a language the category is not configured
// for, so guessing is worse than an empty required field). Languages the agent has already used
// are dropped first - preselecting one would put the form in a state the API refuses.
export function resolveSoftphoneCampaignLanguageId(categoryLanguages, currentLanguageId, takenLanguageIds = []) {
  const languages = (categoryLanguages ?? []).filter(language => !takenLanguageIds.includes(language.id))

  if (languages.length === 1) {
    return languages[0].id
  }

  const supports = languageId => languages.some(language => language.id === languageId)

  if (currentLanguageId && supports(currentLanguageId)) {
    return currentLanguageId
  }

  return supports(SOFTPHONE_CAMPAIGN_PREFERRED_LANGUAGE_ID) ? SOFTPHONE_CAMPAIGN_PREFERRED_LANGUAGE_ID : null
}

export function isSoftphoneCampaign(campaign) {
  return campaign?.routing?.type === campaignRoutingTypes.Static
    && campaign?.routing?.destinationType === destinationTypes.Softphone
}

// The softphone creation page hides Basic Details, so the required campaign name is derived
// from the two things that identify it to the buyer: the category and who set it up.
export function buildSoftphoneCampaignName(categoryName, userName) {
  return ['Softphone', categoryName, userName].filter(part => Boolean(part)).join(' ')
}

// The Routing column shows what the buyer picked, not the internal routing type.
export function getCampaignRoutingLabel(campaign, startCase) {
  return isSoftphoneCampaign(campaign) ? 'Softphone' : startCase(campaign?.routing?.type)
}

export function getDefaultParser() {
  return {
    parserType: 'jpath'
  }
}

export function convertCurrencyStringToNumber(currencyString) {
  if (currencyString == null) {
    return currencyString
  }
  
  currencyString = currencyString.toString()
  return Number(currencyString.replace(/[$,]/g, ''))
}

/**
 * The badge colour, and it follows the same precedence as the label below: REVIEW OUTRANKS THE
 * PAUSE. An unapproved campaign cannot take calls however it is paused, so a paused campaign
 * awaiting review wears the same blue as any other campaign in review - the pause is the detail,
 * the review is the thing the buyer is waiting on.
 *
 * A wrap-up pause is a plain yellow pause. It used to be blue, on the reasoning that it is
 * transient and the agent is still live, but blue is the review colour: two unrelated states
 * sharing one colour is worse than a wrap-up looking like the pause it is.
 */
export function getBadgeVariantForCampaignStatus(campaign) {
  if (!campaign) {
    return 'default'
  }

  if (campaign.status === IN_REVIEW_STATUS) {
    return 'info'
  }

  if (campaign.isPaused) {
    return 'warning'
  }

  switch (campaign.status) {
  case ACTIVE_STATUS:
    return 'success'
  case PAUSED_STATUS:
    return 'warning'
  case 'Rejected':
    return 'danger'
  default:
    return 'default'
  }
}

/**
 * A wrap-up pause is the AGENT's state, not the campaign's, and the server keeps the pause labelled
 * as wrap-up until something resumes the campaign. Nothing ever does when the widget window is
 * closed mid wrap-up: go-offline only touches campaigns that are still Active, so the row keeps
 * reading "Paused (wrap-up)" for a wrap-up that ended with the window. The label is therefore only
 * honoured while this session's softphone is really holding a wrap-up pause for that campaign - any
 * other pause is just a pause.
 */
export function isCampaignPausedForWrapUp(campaign, softphoneState) {
  return Boolean(campaign?.isWrapUpPause)
    && Boolean(softphoneState?.isPausedForWrapUp)
    && softphoneState?.liveCampaign?.id === campaign?.id
}

/**
 * Applied to every campaign as it arrives from the server, so a stale wrap-up label cannot survive
 * a refetch. Returns the campaign untouched when the flag already agrees, to keep the common case
 * free of pointless copies.
 */
function withResolvedWrapUpPause(campaign, softphoneState) {
  const isWrapUpPause = isCampaignPausedForWrapUp(campaign, softphoneState)

  if (Boolean(campaign?.isWrapUpPause) === isWrapUpPause) {
    return campaign
  }

  return { ...campaign, isWrapUpPause }
}

/**
 * Every qualifier is a parenthetical on the state that outranks it, so the first word of the badge
 * is always the state that decides what the campaign can do.
 *
 * Review comes first: a paused campaign awaiting review reads "In Review (Paused)" rather than a
 * bare "Paused", which hid the review entirely and left the buyer waiting on something they could
 * not see. A wrap-up is the same shape one level down - the campaign is paused, and the wrap-up
 * says why.
 */
export function getStatusText(campaign) {
  if (!campaign) {
    return ''
  }

  if (campaign.status === IN_REVIEW_STATUS) {
    return campaign.isPaused ? 'In Review (Paused)' : 'In Review'
  }

  if (campaign.isPaused) {
    return campaign.isWrapUpPause ? 'Paused (wrap-up)' : 'Paused'
  }

  return campaign.status
}


export function getRtbTagsWithDescription() {
  const rtbTags = [{
    key: "trafficsource",
    value: "trafficsource",
    description: "Traffic Source"
  },
  {
    key: "thirdparty",
    value: "thirdparty",
    description: "Third Party"
  },
  {
    key: "age",
    value: "age",
    description: "Age"
  },
  {
    key: "homeowner",
    value: "homeowner",
    description: "Homeowner"
  },
  {
    key: "state",
    value: "state",
    description: "US state"
  },
  {
    key: "zipcode",
    value: "zipcode",
    description: "ZIP code"
  },  
  {
    key: "creative",
    value: "creative",
    description: "Creative"
  },
  {
    key: "channel",
    value: "channel",
    description: "Channel Name"
  }]

  const tagDescriptionMapping = new Map(rtbTags.map(tag => [tag.key, tag.description]))

  return { rtbTags, tagDescriptionMapping }
}

export function transformBuyerCampaignForAPI(buyerCampaign) {
  const tempCampaignClone = cloneDeep(buyerCampaign)

  tempCampaignClone.audience = {
    ...tempCampaignClone.audience
  }

  tempCampaignClone.budget = {
    ...tempCampaignClone.budget,
    dailyBudgetInUSD: convertCurrencyStringToNumber(tempCampaignClone.budget.dailyBudgetInUSD),
    hourlyBudgetInUSD: tempCampaignClone.budget.hourlyBudgetInUSD
      ? convertCurrencyStringToNumber(tempCampaignClone.budget.hourlyBudgetInUSD) : 0,
    // maxBidPerCallInUSD is required by the backend and must be in range 1.0-9999.99
    // It will default to 1 only if the field is not visible in UI and validation does not force user to enter the value, like for RTB campaigns
    maxBidPerCallInUSD: convertCurrencyStringToNumber(tempCampaignClone.budget.maxBidPerCallInUSD),
    isPostbackConversion: tempCampaignClone.budget.isPostbackConversion || false
  }

  if (tempCampaignClone.routing.destinationType === destinationTypes.Softphone) {
    // Provisioned per agent - the buyer supplies neither a number nor SIP details.
    tempCampaignClone.routing.number = ''
    tempCampaignClone.routing.sipNumber = {}
  } else if (tempCampaignClone.routing.destinationType === destinationTypes.SIP) {
    tempCampaignClone.routing.number = ''
  } else {
    tempCampaignClone.routing.sipNumber = {}
  }

  if (tempCampaignClone.routing.type === 'Programmatic') {
    const tempProgrammaticTarget = {
      ...tempCampaignClone.routing.programmaticTarget
    }

    const transformedHeaders = tranformHeaders(tempProgrammaticTarget.headers)

    if (!tempProgrammaticTarget.bidSettings.isBidDynamic) {
      delete tempProgrammaticTarget.dynamicBidAmountParser
    }

    if (!tempProgrammaticTarget.bidSettings.conversionCallLengthDynamic) {
      delete tempProgrammaticTarget.dynamicCallLengthSettingParser
    }

    if (!tempProgrammaticTarget.isNumberDynamic) {
      delete tempProgrammaticTarget.dynamicDialSettingParser
    }

    tempProgrammaticTarget.bidSettings = {
      ...tempProgrammaticTarget.bidSettings,
      staticBidAmount: convertCurrencyStringToNumber(tempProgrammaticTarget.bidSettings.staticBidAmount),
      bidOnFailure: convertCurrencyStringToNumber(tempProgrammaticTarget.bidSettings.bidOnFailure)
    }

    tempProgrammaticTarget.headers = transformedHeaders

    tempCampaignClone.routing = {
      ...tempCampaignClone.routing,
      programmaticTarget: tempProgrammaticTarget
    }
  } else {
    const tempRingbaRTBTarget = {
      ...tempCampaignClone.routing.ringbaRTB
    }

    let transformedTagMappings = null

    if (tempRingbaRTBTarget.tagMappings?.length) {
      transformedTagMappings = tranformHeaders(tempRingbaRTBTarget.tagMappings)
    }

    tempRingbaRTBTarget.tagMappings = transformedTagMappings
    tempRingbaRTBTarget.defaultPostbackBidAmount = convertCurrencyStringToNumber(tempRingbaRTBTarget.defaultPostbackBidAmount)

    tempCampaignClone.routing = {
      ...tempCampaignClone.routing,
      programmaticTarget: null,
      ringbaRTB: tempRingbaRTBTarget
    }
  }

  if (tempCampaignClone.routing.type !== 'RingbaRTB') {
    tempCampaignClone.routing = {
      ...tempCampaignClone.routing,
      ringbaRTB: null
    }
  }

  tempCampaignClone.trafficSources = {
    ...tempCampaignClone.trafficSources,
    allowedSources: tempCampaignClone.trafficSources.allowedSources.map(id => {
      return {
        id
      }
    })
  }

  return tempCampaignClone
}

function tranformHeaders(headers) {
  let tempHeaders = {}

  headers?.forEach(header => {
    tempHeaders[header.key] = header.value
  })

  return tempHeaders
}


function transformHeadersFromAPIToUI(headers) {
  return  headers
    ? Object.entries(headers).map(entry => ({
      key: entry[0],
      value: entry[1]
    }))
    : []

}

function getDefaultProgrammaticTarget() {
  return {
    bidSettings: {
      staticBidAmount: '$1',
      bidOnFailure: '$0.5',
      bidType: 'Static',
      isBidDynamic: false,
      conversionCallLengthDynamic: false,
      conversionCallLengthInSeconds: 1,
      maxConversionCallDurationSeconds: 1,
    },
    destinationType: 'Number',
    number: '',
    sipNumber: {},
    method: null,
    isNumberDynamic: false,
    headers: [],

    dynamicBidAmountParser: [getDefaultParser()],
    shouldAcceptCallParser: [getDefaultParser()],
    dynamicDialSettingParser: [getDefaultParser()],
    dynamicCallLengthSettingParser: [getDefaultParser()],
  }
}

function getDefaultBuyerCampaign() {
  return {
    categoryId: null,
    countryId: null,
    languageId: null,
    name: '',
    routing: {
      type: 'Static',
      destinationType: 'Number',
      number: '+1',
      sipNumber: {},
      programmaticTarget: getDefaultProgrammaticTarget(),
      ringbaRTB: {},

      schedule: {
        hoursOfOperation: null,
        timeZoneId: null,
        concurrencyCap: 1,
        hourlyConcurrencyCap: null
      },
    },
    trafficSources: {
      callType: 'Inbound',
      requirements: 'None',
      allowThirdParties: true,
      allowTollFreeCallers: true,
      restrictDuplicates: 'InheritByBuyer',
      allowedSources: []
    },
    audience: {      
      searchLocation: false,
      selectedLocations: [],
      locationFilterType: 'States',
      noIcp: true, 
      phoneType: '',    
    },

    // integrations: {},

    budget: {
      minimumDurationSelected: true,
      minimumDurationInSeconds: 90,
      hourlyBudgetSelected: false,
      isPostbackConversion: false
      //Do not "suggest" any monetary values here
      // dailyBudgetInUSD: '$10',
      // maxBidPerCallInUSD: '$1'
    }
  }
}

export function transformBuyerCampaignFromAPIForUI(campaign) {
  let transformedCampaign = {...campaign}

  if (!transformedCampaign.routing.programmaticTarget) {
    transformedCampaign.routing.programmaticTarget = getDefaultProgrammaticTarget()
  }

  if (transformedCampaign.routing.type === 'Programmatic') {
    transformedCampaign.routing.programmaticTarget.headers = transformHeadersFromAPIToUI(transformedCampaign.routing.programmaticTarget.headers)
  }

  if (transformedCampaign.routing.type === 'RingbaRTB') {
    transformedCampaign.routing.ringbaRTB.tagMappings = transformHeadersFromAPIToUI(transformedCampaign.routing.ringbaRTB.tagMappings)
    if (transformedCampaign.routing.ringbaRTB.defaultPostbackBidAmount != null) {
      transformedCampaign.routing.ringbaRTB.defaultPostbackBidAmount = currencyFormatter(transformedCampaign.routing.ringbaRTB.defaultPostbackBidAmount)
    }
  }

  const programmaticTarget = transformedCampaign.routing.programmaticTarget

  if (!programmaticTarget.dynamicBidAmountParser) {
    programmaticTarget.dynamicBidAmountParser = [getDefaultParser()]
  }

  if (!programmaticTarget.shouldAcceptCallParser) {
    programmaticTarget.shouldAcceptCallParser = [getDefaultParser()]
  }

  if (!programmaticTarget.dynamicDialSettingParser) {
    programmaticTarget.dynamicDialSettingParser = [getDefaultParser()]
  }

  if (!programmaticTarget.dynamicCallLengthSettingParser) {
    programmaticTarget.dynamicCallLengthSettingParser = [getDefaultParser()]
  }

  programmaticTarget.bidSettings.bidType = programmaticTarget.bidSettings.isBidDynamic ? 'Dynamic' : 'Static'
  
  if (!programmaticTarget.bidSettings.isBidDynamic) {
    programmaticTarget.bidSettings.staticBidAmount = currencyFormatter(programmaticTarget.bidSettings.staticBidAmount)
    programmaticTarget.bidSettings.bidOnFailure = currencyFormatter(programmaticTarget.bidSettings.bidOnFailure)
  }

  transformedCampaign.routing.ringbaRTB =  {
    ...campaign.routing.ringbaRTB
  }

  transformedCampaign.categoryId = campaign.category.id
  transformedCampaign.countryId = campaign.country.id
  transformedCampaign.languageId = campaign.language.id

  transformedCampaign.trafficSources.allowedSources = transformedCampaign.trafficSources.allowedSources.map(ts => ts.id)

  transformedCampaign.budget.dailyBudgetInUSD = currencyFormatter(transformedCampaign.budget.dailyBudgetInUSD)
  transformedCampaign.budget.maxBidPerCallInUSD = currencyFormatter(transformedCampaign.budget.maxBidPerCallInUSD)
  transformedCampaign.budget.isPostbackConversion = campaign.budget.isPostbackConversion || false

  if (transformedCampaign.budget.hourlyBudgetSelected) {
    transformedCampaign.budget.hourlyBudgetInUSD = currencyFormatter(transformedCampaign.budget.hourlyBudgetInUSD)
  }

  transformedCampaign.audience.searchLocation = campaign.audience.searchLocation
  transformedCampaign.audience.selectedLocations = campaign.audience.selectedLocations
  
  return transformedCampaign
}

const state = {
  buyerCampaigns: [],
  buyerCampaign: getDefaultBuyerCampaign(),
  // Limits the campaign had before switching off Static routing. Loading a campaign drops them
  buyerCampaignStaticLimits: null
}

const getters = {
  isProgrammaticBuyerCampaign(state) {
    return state.buyerCampaign?.routing?.type === campaignRoutingTypes.Programmatic
  },

  isRingbaRTBBuyerCampaign(state) {
    return state.buyerCampaign?.routing?.type === campaignRoutingTypes.RingbaRTB
  }
}

const actions = {
  async createBuyerCampaign({ rootGetters }, campaign) {
    const response = await CampaignAPI.createBuyerCampaign({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaign: transformBuyerCampaignForAPI(campaign)
    })

    return response.data
  },

  async fetchBuyerCampaigns({ commit, rootGetters, rootState }) {
    const response = await CampaignAPI.listBuyerCampaigns(
      rootGetters['auth/accountDetails']?.id,
    )

    const campaigns = Array.isArray(response.data)
      ? response.data.map(campaign => withResolvedWrapUpPause(campaign, rootState.softphone))
      : response.data

    commit('SET_BUYER_CAMPAIGNS', campaigns)
  },

  async fetchBuyerCampaign({ commit, rootGetters, rootState, dispatch }, id) {
    const response = await CampaignAPI.getBuyerCampaign(
      rootGetters['auth/accountDetails']?.id,
      id
    )

    commit('SET_BUYER_CAMPAIGN', withResolvedWrapUpPause(
      transformBuyerCampaignFromAPIForUI(response.data),
      rootState.softphone
    ))

    if (response.data.isDailyBudgetReached) {
      dispatch('showDashboardNotification', {
        text: 'Your campaign daily budget is exceeded. ',      
        hideOnClick: false
      }, { root:true })
    }
  },

  /**
   * Whether the campaign has spent its own daily budget, fetched fresh and committed NOWHERE.
   *
   * Its own action rather than a call to `fetchBuyerCampaign`, which commits the campaign into the
   * single-campaign slot the edit form reads and raises a dashboard notification of its own. Both
   * are wrong for a caller that only wants to know whether calls can still be bought: one would
   * overwrite whatever the agent has open, and the other would put a banner up from a poll.
   *
   * The flag lives on the single-campaign endpoint alone - see `getSoftphoneGoLiveBlockedReason` -
   * so this is the only way to get at it.
   *
   * The pause flags ride along because the softphone polls this for the length of a shift and is
   * the only thing that can notice the campaign being paused under it - by the system after
   * unanswered calls, or by an administrator. `isWrapUpPause` is what lets it tell those apart
   * from its own wrap-up pause.
   */
  async fetchBuyerCampaignBudgetState({ rootGetters }, campaignId) {
    const response = await CampaignAPI.getBuyerCampaign(
      rootGetters['auth/accountDetails']?.id,
      campaignId
    )

    const campaign = response.data || {}

    return {
      isDailyBudgetReached: campaign.isDailyBudgetReached === true,
      isPaused: campaign.isPaused === true,
      isStrictPause: campaign.isStrictPause === true,
      strictPauseReason: campaign.strictPauseReason || null,
      isWrapUpPause: campaign.isWrapUpPause === true
    }
  },

  async pauseBuyerCampaign({ commit, rootGetters }, { campaignId }) {
    const response = await CampaignAPI.pauseBuyerCampaign({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaignId
    })

    commit('UPDATE_BUYER_CAMPAIGN_STATUS_IN_LIST', { campaignId, isPaused: true })

    return response.data
  },

  async resumeBuyerCampaign({ commit, rootGetters }, { campaignId, status }) {
    const response = await CampaignAPI.resumeBuyerCampaign({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaignId
    })

    commit('UPDATE_BUYER_CAMPAIGN_STATUS_IN_LIST', {
      campaignId,
      isPaused: false,
      status: status === PAUSED_STATUS ? ACTIVE_STATUS : status
    })

    return response.data
  },

  /**
   * Softphone Go Live. One server call pauses the agent's other live softphone campaigns and resumes
   * this one. The selected row is updated in place so the button flips immediately; the rest of the
   * list is refetched because which OTHER campaigns the server paused is only known there.
   */
  async goLiveSoftphoneCampaign({ commit, dispatch, rootGetters }, { campaignId, status }) {
    const response = await SoftphoneAPI.goLive(rootGetters['auth/accountDetails']?.id, campaignId)

    commit('UPDATE_BUYER_CAMPAIGN_STATUS_IN_LIST', {
      campaignId,
      isPaused: false,
      status: status === PAUSED_STATUS ? ACTIVE_STATUS : status
    })

    await dispatch('refreshBuyerCampaignsIfLoaded')

    return response.data
  },

  /**
   * Softphone Go Offline. The server pauses every live softphone campaign ringing the agent, so this
   * takes no campaign id. Dispatched from the widget window too, where no list is loaded - hence the
   * conditional refresh rather than an unconditional fetch.
   */
  async goOfflineSoftphone({ dispatch, rootGetters }) {
    const response = await SoftphoneAPI.goOffline(rootGetters['auth/accountDetails']?.id)

    await dispatch('refreshBuyerCampaignsIfLoaded')

    return response.data
  },

  /**
   * Post-call wrap-up, pause half. Dispatched by the softphone once an answered call has run long
   * enough that the agent will need a breather after it. Same server call as Go Offline - every
   * softphone campaign ringing the agent is paused - with a reason so the campaign's audit note says
   * wrap-up rather than that the agent walked away.
   *
   * Deliberately does NOT refresh the list, which every other softphone action here does. The
   * wrap-up label is resolved at fetch time against the softphone's own state (see
   * `isCampaignPausedForWrapUp`), and that state does not say "wrapping up" until this call has
   * returned - so a refetch from in here would strip the very label it went to fetch. The caller
   * refreshes once the flag is set; see `pauseForWrapUp` in the softphone store.
   */
  async pauseSoftphoneForWrapUp({ rootGetters }) {
    const response = await SoftphoneAPI.goOffline(rootGetters['auth/accountDetails']?.id, {
      reason: SOFTPHONE_STATE_CHANGE_REASON.WRAP_UP
    })

    return response.data
  },

  /**
   * Post-call wrap-up, resume half. Same server call as Go Live for the campaign the agent was live
   * on. No local list mutation: this runs in the widget window, which has no list, and the dashboard
   * tabs refetch when the widget's broadcast tells them the wrap-up pause lifted.
   */
  async resumeSoftphoneAfterWrapUp({ dispatch, rootGetters }, { campaignId }) {
    const response = await SoftphoneAPI.goLive(rootGetters['auth/accountDetails']?.id, campaignId, {
      reason: SOFTPHONE_STATE_CHANGE_REASON.WRAP_UP
    })

    await dispatch('refreshBuyerCampaignsIfLoaded')

    return response.data
  },

  /**
   * Best effort: a stale list is a cosmetic problem, a failed go-live/go-offline is not, so a refetch
   * error must not fail the caller.
   */
  async refreshBuyerCampaignsIfLoaded({ state, dispatch }) {
    if (!state.buyerCampaigns?.length) {
      return
    }

    try {
      await dispatch('fetchBuyerCampaigns')
    } catch (error) {
      console.error('Failed to refresh buyer campaigns after a softphone state change', error)
    }
  },

  // Resume a buyer campaign for an explicitly provided account, used when the
  // user arrives from an email link where the current account context may not
  // match the campaign's account.
  async resumeBuyerCampaignByAccount(context, { accountId, campaignId }) {
    const response = await CampaignAPI.resumeBuyerCampaign({ accountId, campaignId })

    return response.data
  },

  async pauseSellerCampaign(context, { campaignId }) {
    const response = await CampaignAPI.pauseSellerCampaign({
      campaignId
    })
    return response.data
  },

  async resumeSellerCampaign(context, { campaignId }) {
    const response = await CampaignAPI.resumeSellerCampaign({
      campaignId
    })
    return response.data
  },

  async updateCampaignBid({ state, commit, rootGetters }, { campaignId, bidValue }) {
    const roundedBidValue = Number(Number(bidValue).toFixed(2))

    // Every bid write comes through here, so no caller can save a static bid below the minimum
    const minBid = getStaticCampaignMinBid(state.buyerCampaigns?.find(campaign => campaign.id === campaignId))
    if (roundedBidValue < minBid) {
      throw new Error(getMinBidMessage(minBid))
    }

    await CampaignAPI.updateCampaignBid({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaignId,
      bidValue: roundedBidValue
    })

    // The endpoint answers with an empty body, so the list learns the new bid from here. Without
    // it every reader of the row - the raise chip, the Confirm Bid Change modal - kept the bid
    // from before an inline edit, and "raising" a $30 bid to a $15 suggestion lowered it.
    commit('UPDATE_BUYER_CAMPAIGN_BID_IN_LIST', { campaignId, bidValue: roundedBidValue })
  },

  async editBuyerCampaign({ rootGetters }, campaign) {
    campaign.country = campaign.countryId
    campaign.category = campaign.categoryId
    campaign.language = campaign.languageId    

    if (!campaign.audience.wirelessCarrierSelected) {
      campaign.audience.wirelessCarriers = []
    }
    if (!campaign.audience.lineTypeSelected) {
      campaign.audience.lineTypes = []
    }

    const response = await CampaignAPI.editBuyerCampaign({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaign: transformBuyerCampaignForAPI(campaign)
    })

    return response.data
  },

  updateDynamicTargetInBuyerCampaign({ commit }, target) {
    commit('UPDATE_BUYER_CAMPAIGN_DYNAMIC_TARGET', target)
  },

  setDefaultBuyerCampaign({ commit }) {
    commit('SET_DEFAULT_BUYER_CAMPAIGN')
  },

  async uploadZipCodesFile({ rootGetters }, file) {    
    const response = await CampaignAPI.uploadZipCodesFile({
      accountId: rootGetters['auth/accountDetails']?.id,
      file: file
    })
    
    return response.data
  }
}

const mutations = {
  SET_DEFAULT_BUYER_CAMPAIGN(state) {
    state.buyerCampaign = getDefaultBuyerCampaign()
    state.buyerCampaignStaticLimits = null
  },

  SET_BUYER_CAMPAIGN(state, campaign) {
    state.buyerCampaign = campaign
    state.buyerCampaignStaticLimits = null
  },

  SET_BUYER_CAMPAIGN_STATIC_LIMITS(state, limits) {
    state.buyerCampaignStaticLimits = limits
  },

  SET_BUYER_CAMPAIGNS(state, campaigns) {
    state.buyerCampaigns = campaigns
  },

  UPDATE_BUYER_CAMPAIGN_STATUS_IN_LIST(state, { campaignId, status, isPaused }) {
    const buyerCampaignIndex = state.buyerCampaigns.findIndex(c => c.id === campaignId)

    if (status) {
      state.buyerCampaigns[buyerCampaignIndex].status = status
    }
    
    state.buyerCampaigns[buyerCampaignIndex].isPaused = isPaused

    state.buyerCampaigns = [...state.buyerCampaigns]
  },

  // A campaign that is not in the list (the list has not loaded) has nothing to keep in step
  UPDATE_BUYER_CAMPAIGN_BID_IN_LIST(state, { campaignId, bidValue }) {
    state.buyerCampaigns = state.buyerCampaigns.map(campaign => campaign.id === campaignId
      ? { ...campaign, budget: { ...campaign.budget, maxBidPerCallInUSD: bidValue } }
      : campaign)
  },

  UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS(state, { name, categoryId, countryId, languageId }) {
    state.buyerCampaign = {
      ...state.buyerCampaign,
      name,
      categoryId,
      countryId,
      languageId
    }
  },
  UPDATE_BUYER_CAMPAIGN_ROUTING(state, routing) {
    state.buyerCampaign.routing = routing
  },
  UPDATE_BUYER_CAMPAIGN_DYNAMIC_TARGET(state, programmaticTarget) {
    state.buyerCampaign.routing.programmaticTarget = programmaticTarget
  },
  UPDATE_BUYER_CAMPAIGN_TRAFFIC_SOURCES(state, trafficSources) {
    state.buyerCampaign.trafficSources = trafficSources
  },
  UPDATE_BUYER_CAMPAIGN_AUDIENCE(state, audience) {
    state.buyerCampaign.audience = audience
  },
  UPDATE_BUYER_CAMPAIGN_INTEGRATIONS(state, integrations) {
    state.buyerCampaign.integrations = integrations
  },
  UPDATE_BUYER_CAMPAIGN_BUDGET(state, budget) {
    state.buyerCampaign.budget = budget
  },
  CLEAR_BUYER_CAMPAIGN_ZIP_CODES_FILE_LINK(state) {
    state.buyerCampaign.zipCodesFileLink = null
  },

  RESET_BUYER_CAMPAIGN_MINIMUM_DURATION(state) {
    state.buyerCampaign.budget = {
      ...state.buyerCampaign.budget,
      minimumDurationInSeconds: 1,
      minimumDurationSelected: false,
      hourlyBudgetSelected: false
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
